import * as React from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useFormikContext } from 'formik';

import { defaultCompare as naturalSort } from 'logic/DefaultCompare';
import ArchiveLicenseStatusContainer from 'archive/components/ArchiveLicenseStatusContainer';
import { ButtonGroup, Button, ControlLabel, FormGroup, HelpBlock, Input, Alert } from 'components/bootstrap';
import { TIME_BASED_SIZE_OPTIMIZING_ROTATION_STRATEGY } from 'stores/indices/IndicesStore';
import type { RetentionJsonSchema } from 'components/indices/Types';

const StyledAlert = styled(Alert)`
  overflow: auto;
  margin-right: 15px;
  margin-left: 15px;
`;

type Props = {
  config: {
    max_number_of_indices: number,
    index_action: string,
  },
  jsonSchema: RetentionJsonSchema,
  updateConfig: Function,
  useMaxNumberOfIndices: () => [
    number | undefined,
    React.Dispatch<React.SetStateAction<number>>
  ],
};

const ArchiveRetentionStrategyConfiguration = ({ config, jsonSchema, updateConfig, useMaxNumberOfIndices }: Props) => {
  const [maxNumberOfIndices, setMaxNumberOfIndices] = useMaxNumberOfIndices();

  const [configuration, setConfiguration] = useState(config);
  const {
    values: {
      rotation_strategy_class: rotationStrategyClass,
    },
  } = useFormikContext<any>();

  const onMaxIndicesUpdate = (e) => {
    const { value } = e.target;
    const update = configuration;

    update.max_number_of_indices = value;

    setMaxNumberOfIndices(value);
    setConfiguration(update);
    updateConfig(update);
  };

  const setIndexAction = (type: string) => () => {
    setConfiguration({ ...configuration, index_action: type });
    updateConfig({ ...configuration, index_action: type });
  };

  const indexActionActive = (type) => {
    const { index_action } = configuration;

    return index_action === type;
  };

  const hasValidSchema = () => !!jsonSchema;

  const indexActions = () => {
    if (hasValidSchema()) {
      const newIndexActions = jsonSchema.properties.index_action.enum;
      newIndexActions.sort((a, b) => naturalSort(a.toLowerCase(), b.toLowerCase()));

      return newIndexActions;
    }

    return [];
  };

  // Get all available index actions from the JSON schema.
  const buttons = indexActions()?.map((action) => {
    const isActive = indexActionActive(action);

    return (
      <Button key={`button-${action}`}
              onClick={setIndexAction(action)}
              active={isActive}
              bsStyle={isActive ? 'info' : 'default'}>
        {action}
      </Button>
    );
  });

  return (
    <div>
      <ArchiveLicenseStatusContainer summary />
      <StyledAlert>
        The archive index retention strategy creates an archive for an index before deleting or closing it.
      </StyledAlert>
      {rotationStrategyClass !== TIME_BASED_SIZE_OPTIMIZING_ROTATION_STRATEGY && (
      <fieldset>
        <Input type="number"
               id="max-number-of-indices"
               label="Max number of indices"
               labelClassName="col-sm-3"
               wrapperClassName="col-sm-9"
               onChange={onMaxIndicesUpdate}
               value={maxNumberOfIndices}
               help={<span>Maximum number of indices to keep before <strong>archiving</strong> the oldest ones</span>}
               autoFocus
               required />
      </fieldset>
      )}
      {hasValidSchema() && (
        <FormGroup id="action-after-archive">
          <ControlLabel className="col-sm-3">Action after archiving index</ControlLabel>
          <div className="col-sm-9">
            <ButtonGroup bsSize="small">{buttons}</ButtonGroup>
            <HelpBlock>Please select which action should be performed once an index has been archived.</HelpBlock>
          </div>
        </FormGroup>
      )}
    </div>
  );
};

ArchiveRetentionStrategyConfiguration.propTypes = {
  config: PropTypes.object.isRequired,
  jsonSchema: PropTypes.any,
  updateConfig: PropTypes.func.isRequired,
};

ArchiveRetentionStrategyConfiguration.defaultProps = {
  jsonSchema: undefined,
};

export default ArchiveRetentionStrategyConfiguration;
